import { CONFIG } from "../../../config";
import { paths } from "../../../routes/paths";
import { getHotspotType, HOTSPOT_TYPES, isTextHotspot } from "./hotspotTypes";
import { DEFAULT_FOV, MAX_FOV, MIN_FOV } from "./threeHelpers";

export {
  getHotspotType,
  HOTSPOT_TYPE_OPTIONS,
  HOTSPOT_TYPES,
  isTextHotspot,
} from "./hotspotTypes";

export { DEFAULT_FOV, MAX_FOV, MIN_FOV };
export const PITCH_LIMIT_DEG = 85;
export const YAW_LIMIT_DEG = 180;

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toFiniteNumber = (value, fallback) => {
  const n = Number(value);
  return value != null && value !== "" && Number.isFinite(n) ? n : fallback;
};

export const SCENE_MEDIA_TYPES = Object.freeze({
  IMAGE: "image",
  VIDEO: "video",
});

export const SCENE_PROJECTIONS = Object.freeze({
  EQUIRECTANGULAR_360: "equirectangular_360",
  EQUIRECTANGULAR_180: "equirectangular_180",
});

export const getSceneProjection = scene => {
  if (
    scene?.mediaType === SCENE_MEDIA_TYPES.VIDEO ||
    scene?.media_type === SCENE_MEDIA_TYPES.VIDEO
  ) {
    return SCENE_PROJECTIONS.EQUIRECTANGULAR_360;
  }
  return scene?.projection === SCENE_PROJECTIONS.EQUIRECTANGULAR_180
    ? SCENE_PROJECTIONS.EQUIRECTANGULAR_180
    : SCENE_PROJECTIONS.EQUIRECTANGULAR_360;
};

export const is180Scene = scene =>
  getSceneProjection(scene) === SCENE_PROJECTIONS.EQUIRECTANGULAR_180;

export const is360Scene = scene =>
  getSceneProjection(scene) === SCENE_PROJECTIONS.EQUIRECTANGULAR_360;

export const normalizeDefaultView = (
  view = {},
  projection = SCENE_PROJECTIONS.EQUIRECTANGULAR_360
) => ({
  yaw:
    projection === SCENE_PROJECTIONS.EQUIRECTANGULAR_180
      ? 0
      : clamp(toFiniteNumber(view.yaw, 0), -YAW_LIMIT_DEG, YAW_LIMIT_DEG),
  pitch: clamp(toFiniteNumber(view.pitch, 0), -PITCH_LIMIT_DEG, PITCH_LIMIT_DEG),
  fov: clamp(toFiniteNumber(view.fov, DEFAULT_FOV), MIN_FOV, MAX_FOV),
});

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const ACCEPTED_VIDEO_TYPES = ["video/mp4"];
export const MAX_VIDEO_SIZE_BYTES = 300_000_000;
export const MAX_VIDEO_DURATION_SECONDS = 300;

export const getSceneMediaType = scene =>
  scene?.mediaType === SCENE_MEDIA_TYPES.VIDEO ||
  scene?.media_type === SCENE_MEDIA_TYPES.VIDEO
    ? SCENE_MEDIA_TYPES.VIDEO
    : SCENE_MEDIA_TYPES.IMAGE;

export const isVideoScene = scene =>
  getSceneMediaType(scene) === SCENE_MEDIA_TYPES.VIDEO;

export const isImageScene = scene =>
  getSceneMediaType(scene) === SCENE_MEDIA_TYPES.IMAGE;

export const createId = (prefix = "") =>
  `${prefix}${crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`}`;

export const stripExtension = (filename = "") =>
  filename.replace(/\.[^/.]+$/, "");

export const isPersistedId = id => {
  const n = Number(id);
  return Number.isInteger(n) && n > 0;
};

export const toPersistedId = id => (isPersistedId(id) ? Number(id) : null);

export const isBlobUrl = value =>
  typeof value === "string" && value.startsWith("blob:");

export const isPersistableImagePath = path =>
  Boolean(path) && !isBlobUrl(path);

export const isPersistableVideoPath = path =>
  Boolean(path) && !isBlobUrl(path);

export const resolvePanoramaUrl = path => {
  if (!path) return "";
  if (isBlobUrl(path) || path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = String(CONFIG.S3_BASE_URI || "").replace(/\/+$/, "");
  const cleaned = String(path).replace(/^\/+/, "");
  return base ? `${base}/${cleaned}` : `/${cleaned}`;
};

export const resolveSceneMediaUrl = scene =>
  isVideoScene(scene)
    ? scene?.videoUrl || resolvePanoramaUrl(scene?.video)
    : scene?.imageUrl || resolvePanoramaUrl(scene?.image);

export const getTourEditorPath = (title, id) =>
  `${paths.admin.tours.create}?title=${encodeURIComponent(title ?? "")}&Id=${id}`;

const extractFirstErrorString = value => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = extractFirstErrorString(item);
      if (found) return found;
    }
  } else if (typeof value === "object") {
    for (const item of Object.values(value)) {
      const found = extractFirstErrorString(item);
      if (found) return found;
    }
  }
  return null;
};

export const getApiErrorMessage = (error, fallback = "Something went wrong") => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 404) return "Tour not found";
  if (status === 403) return "Invalid or expired session. Please sign in again.";
  if (status === 502) {
    if (data?.message && data?.error) return `${data.message} (${data.error})`;
    return data?.message || "Failed to process 360 media";
  }
  if (typeof data?.message === "string") return data.message;
  if (data) {
    const extracted = extractFirstErrorString(data);
    if (extracted) return extracted;
  }
  return error?.message || fallback;
};

export const getFieldErrors = error => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  const fields = {};
  for (const [key, value] of Object.entries(data)) {
    if (key === "message" || key === "error") continue;
    if (Array.isArray(value) && value[0]) fields[key] = String(value[0]);
    else if (typeof value === "string" && value) fields[key] = value;
  }
  return fields;
};

export const createEmptyTour = () => ({
  id: null,
  title: "Untitled Tour",
  startSceneId: null,
  scenes: [],
});

export const createSceneFromUpload = (file, data, nextOrder, extra = {}) => {
  const mediaType =
    data?.mediaType === SCENE_MEDIA_TYPES.VIDEO ||
    extra?.mediaType === SCENE_MEDIA_TYPES.VIDEO
      ? SCENE_MEDIA_TYPES.VIDEO
      : SCENE_MEDIA_TYPES.IMAGE;

  const isVideo = mediaType === SCENE_MEDIA_TYPES.VIDEO;
  const projection = isVideo
    ? SCENE_PROJECTIONS.EQUIRECTANGULAR_360
    : getSceneProjection(data?.projection ? data : extra);
  const videoKey = data?.video || extra?.videoKey || "";

  let videoUrl = "";
  if (isVideo) {
    if (videoKey && !extra?.forceBlobUrl) {
      videoUrl = resolvePanoramaUrl(videoKey);
    } else if (extra?.videoUrl) {
      videoUrl = extra.videoUrl;
    } else if (extra?.videoFile) {
      videoUrl = URL.createObjectURL(extra.videoFile);
    } else if (
      file &&
      (file.type?.startsWith("video/") || file.name?.toLowerCase().endsWith(".mp4"))
    ) {
      videoUrl = URL.createObjectURL(file);
    } else if (videoKey) {
      videoUrl = resolvePanoramaUrl(videoKey);
    }
  }

  let imageUrl = "";
  if (data?.path && !extra?.forceBlobUrl) {
    imageUrl = resolvePanoramaUrl(data.path);
  } else if (extra?.posterUrl) {
    imageUrl = extra.posterUrl;
  } else if (extra?.posterBlob) {
    imageUrl = URL.createObjectURL(extra.posterBlob);
  } else if (file && (file.type?.startsWith("image/") || !isVideo)) {
    imageUrl = URL.createObjectURL(file);
  } else if (data?.path) {
    imageUrl = resolvePanoramaUrl(data.path);
  }

  const fallbackName =
    stripExtension(extra?.videoFile?.name) ||
    stripExtension(file?.name) ||
    (isVideo ? "Untitled 360 Video" : "Untitled Scene");

  return {
    id: data.id,
    name: data.title || fallbackName,
    mediaType,
    projection,
    image: data.path,
    imageUrl,
    video: videoKey,
    videoUrl,
    defaultView: normalizeDefaultView(
      { yaw: data.yaw, pitch: data.pitch, fov: data.fov },
      projection
    ),
    order: data.order ?? nextOrder ?? 1,
    hotspots: [],
  };
};

export const isFullUrl = value => /^https?:\/\/\S+$/i.test(String(value ?? "").trim());

export const openHotspotUrl = hotspot => {
  if (!isTextHotspot(hotspot)) return;
  const url = hotspot.webUrl?.trim();
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

export const createHotspotDraft = ({ yaw, pitch, id } = {}) => ({
  id: id ?? createId("hs_"),
  type: HOTSPOT_TYPES.NAVIGATION,
  name: "",
  yaw: yaw ?? 0,
  pitch: pitch ?? 0,
  targetSceneId: "",
  webUrl: "",
  isHidden: false,
});

export const mapHotspotFromApi = (hotspot, parentSceneId) => ({
  id: hotspot.id,
  type: getHotspotType(hotspot),
  name: hotspot.name || "",
  yaw: hotspot.yaw,
  pitch: hotspot.pitch,
  targetSceneId: toPersistedId(hotspot.targetSceneId) ?? "",
  parentSceneId: toPersistedId(hotspot.parentSceneId) ?? parentSceneId,
  webUrl: hotspot.webUrl ?? "",
  isHidden: Boolean(hotspot.isHidden ?? hotspot.is_hidden),
});

export const normalizeHotspotForSave = (hotspot, parentSceneId) => {
  const text = isTextHotspot(hotspot);
  return {
    ...hotspot,
    type: getHotspotType(hotspot),
    name: hotspot.name != null ? String(hotspot.name).trim() : "",
    targetSceneId: text ? null : Number(hotspot.targetSceneId),
    webUrl: hotspot.webUrl?.trim() ?? "",
    parentSceneId,
    isHidden: Boolean(hotspot.isHidden),
  };
};

export const mapSceneFromApi = scene => {
  const id = toPersistedId(scene.id);
  const mediaType =
    scene.mediaType === SCENE_MEDIA_TYPES.VIDEO ||
    scene.media_type === SCENE_MEDIA_TYPES.VIDEO
      ? SCENE_MEDIA_TYPES.VIDEO
      : SCENE_MEDIA_TYPES.IMAGE;
  const isVideo = mediaType === SCENE_MEDIA_TYPES.VIDEO;
  const video = scene.video || "";
  const imageKey = scene.image || scene.path || "";
  const sceneName = scene.name || scene.title || "";

  const projection = isVideo
    ? SCENE_PROJECTIONS.EQUIRECTANGULAR_360
    : getSceneProjection(scene);

  return {
    id,
    name: sceneName,
    mediaType,
    projection,
    image: imageKey,
    imageUrl: resolvePanoramaUrl(imageKey),
    video,
    videoUrl:
      mediaType === SCENE_MEDIA_TYPES.VIDEO && video
        ? resolvePanoramaUrl(video)
        : "",
    defaultView: normalizeDefaultView(
      {
        yaw: scene.yaw ?? scene.defaultView?.yaw,
        pitch: scene.pitch ?? scene.defaultView?.pitch,
        fov: scene.fov ?? scene.defaultView?.fov,
      },
      projection
    ),
    order: scene.order,
    hotspots: (scene.hotspots ?? []).map(hotspot => mapHotspotFromApi(hotspot, id)),
  };
};

export const sortScenesByOrder = (scenes = []) =>
  [...scenes].sort((a, b) => {
    const orderA = Number.isFinite(Number(a?.order)) ? Number(a.order) : 0;
    const orderB = Number.isFinite(Number(b?.order)) ? Number(b.order) : 0;
    if (orderA !== orderB) return orderA - orderB;
    const idA = Number.isFinite(Number(a?.id)) ? Number(a.id) : 0;
    const idB = Number.isFinite(Number(b?.id)) ? Number(b.id) : 0;
    return idA - idB;
  });

export const hydrateTourFromApi = data => {
  const rawScenes = [...(data?.scenes ?? [])];
  const sorted = sortScenesByOrder(rawScenes);
  const scenes = sorted.map(mapSceneFromApi);

  return {
    id: toPersistedId(data?.id),
    title: data?.title || "Untitled Tour",
    startSceneId: scenes[0]?.id ?? toPersistedId(data?.startSceneId) ?? null,
    scenes,
  };
};

export const serializeTourForSave = tour => {
  const persistedScenes = (tour.scenes ?? []).filter(scene => isPersistedId(scene.id));
  const effectiveStartSceneId = persistedScenes[0]?.id ?? tour.startSceneId;

  return {
    title: tour.title,
    startSceneId: toPersistedId(effectiveStartSceneId),
    scenes: persistedScenes.map((scene, index) => {
      const sceneId = Number(scene.id);
      const projection = getSceneProjection(scene);
      const view = normalizeDefaultView(scene.defaultView ?? scene, projection);
      const payload = {
        id: sceneId,
        name: scene.name,
        projection,
        yaw: view.yaw,
        pitch: view.pitch,
        fov: view.fov,
        order:
          Number.isInteger(Number(scene.order)) && Number(scene.order) > 0
            ? Number(scene.order)
            : index + 1,
        hotspots: (scene.hotspots ?? []).map(hotspot => {
          const text = isTextHotspot(hotspot);
          const hotspotPayload = {
            type: getHotspotType(hotspot),
            name: hotspot.name != null ? String(hotspot.name).trim() : "",
            yaw: hotspot.yaw,
            pitch: hotspot.pitch,
            targetSceneId: text ? null : Number(hotspot.targetSceneId),
            parentSceneId: sceneId,
            webUrl: hotspot.webUrl?.trim() || null,
            isHidden: Boolean(hotspot.isHidden),
          };
          const hotspotId = toPersistedId(hotspot.id);
          if (hotspotId) hotspotPayload.id = hotspotId;
          return hotspotPayload;
        }),
      };
      if (isPersistableImagePath(scene.image)) {
        payload.image = scene.image;
      }
      return payload;
    }),
  };
};

export const renameScene = (tour, sceneId, name) => ({
  ...tour,
  scenes: tour.scenes.map(scene =>
    scene.id === sceneId ? { ...scene, name: name.trim() || scene.name } : scene
  ),
});

export const updateSceneDefaultView = (tour, sceneId, patch) => ({
  ...tour,
  scenes: tour.scenes.map(scene =>
    scene.id === sceneId
      ? {
          ...scene,
          defaultView: normalizeDefaultView(
            { ...scene.defaultView, ...patch },
            getSceneProjection(scene)
          ),
        }
      : scene
  ),
});

const revokeUrlIfBlob = url => {
  if (isBlobUrl(url)) URL.revokeObjectURL(url);
};

export const revokeSceneUrls = scene => {
  revokeUrlIfBlob(scene?.imageUrl);
  revokeUrlIfBlob(scene?.videoUrl);
  (scene?.hotspots ?? []).forEach(hs => revokeUrlIfBlob(hs.imageUrl));
};

export const revokeTourUrls = tour => {
  (tour?.scenes ?? []).forEach(revokeSceneUrls);
};

export const reorderScenes = (tour, fromIndex, toIndex) => {
  if (!tour?.scenes) return tour;
  const scenes = [...tour.scenes];
  if (
    fromIndex < 0 ||
    fromIndex >= scenes.length ||
    toIndex < 0 ||
    toIndex >= scenes.length ||
    fromIndex === toIndex
  ) {
    return tour;
  }
  const [movedScene] = scenes.splice(fromIndex, 1);
  scenes.splice(toIndex, 0, movedScene);

  const reorderedScenes = scenes.map((scene, idx) => ({
    ...scene,
    order: idx + 1,
  }));

  return {
    ...tour,
    scenes: reorderedScenes,
    startSceneId: reorderedScenes[0]?.id ?? null,
  };
};

export const deleteScene = (tour, sceneId) => {
  const sceneToDelete = tour.scenes.find(scene => scene.id === sceneId);
  if (sceneToDelete) revokeSceneUrls(sceneToDelete);

  const scenes = tour.scenes
    .filter(scene => scene.id !== sceneId)
    .map((scene, idx) => ({
      ...scene,
      order: idx + 1,
      hotspots: (scene.hotspots ?? []).map(hotspot =>
        Number(hotspot.targetSceneId) === Number(sceneId)
          ? { ...hotspot, targetSceneId: "" }
          : hotspot
      ),
    }));

  const startSceneId = scenes[0]?.id ?? null;

  return { ...tour, scenes, startSceneId };
};

export const getNextActiveSceneId = (scenes, deletedSceneId, currentActiveId) => {
  if (!scenes.length) return null;
  if (currentActiveId && scenes.some(scene => scene.id === currentActiveId)) {
    return currentActiveId;
  }
  const deletedIndex = tourSceneIndex(deletedSceneId, scenes);
  if (deletedIndex >= 0 && scenes[deletedIndex]) return scenes[deletedIndex].id;
  if (deletedIndex > 0) return scenes[deletedIndex - 1].id;
  return scenes[0].id;
};

const tourSceneIndex = (sceneId, scenes) =>
  scenes.findIndex(scene => scene.id === sceneId);

export const upsertHotspot = (tour, activeSceneId, hotspot) => ({
  ...tour,
  scenes: tour.scenes.map(scene => {
    if (scene.id !== activeSceneId) return scene;
    const hotspots = [...(scene.hotspots ?? [])];
    const index = hotspots.findIndex(item => item.id === hotspot.id);
    if (index >= 0) hotspots[index] = hotspot;
    else hotspots.push(hotspot);
    return { ...scene, hotspots };
  }),
});

export const deleteHotspot = (tour, activeSceneId, hotspotId) => {
  const activeScene = tour.scenes.find(scene => scene.id === activeSceneId);
  const hotspot = activeScene?.hotspots?.find(item => item.id === hotspotId);
  revokeUrlIfBlob(hotspot?.imageUrl);

  return {
    ...tour,
    scenes: tour.scenes.map(scene =>
      scene.id === activeSceneId
        ? {
            ...scene,
            hotspots: (scene.hotspots ?? []).filter(item => item.id !== hotspotId),
          }
        : scene
    ),
  };
};

export const revokeHotspotDraftUrls = hotspot => {
  revokeUrlIfBlob(hotspot?.imageUrl);
};

export const getSceneById = (tour, sceneId) =>
  tour.scenes.find(scene => scene.id === sceneId) ?? null;

export const getTargetSceneName = (tour, targetSceneId) =>
  tour.scenes.find(scene => scene.id === targetSceneId)?.name ?? "Unknown";
